import React, { useEffect, useState, useCallback } from 'react';
import { Container } from 'react-bootstrap';

const Question = ({ question, handleAnswer, currentQuestion, totalQuestions, allQuestions }) => {
    const [options, setOptions] = useState([]);
    const [selectedOption, setSelectedOption] = useState(null);
    const [isCorrect, setIsCorrect] = useState(null); // 正解か不正解かを保存する状態

    // 選択肢を生成する関数
    const generateOptions = useCallback((correctAnswer) => {
        const filterCondition = (item) => {
            if (question.category) {
                // カテゴリークイズの場合、同じカテゴリーのものだけを選択肢にする
                return item.japanese !== correctAnswer && item.category === question.category;
            } else {
                // 通常のクイズの場合、同じ品詞のものだけを選択肢にする
                return item.japanese !== correctAnswer && item.part_of_speech === question.part_of_speech;
            }
        };
    
        const incorrectOptions = allQuestions
            .filter(filterCondition)
            .sort(() => 0.5 - Math.random())
            .slice(0, 3)
            .map(item => item.japanese);
    
        const allOptions = [correctAnswer, ...incorrectOptions].sort(() => 0.5 - Math.random());
        setOptions(allOptions);
    }, [allQuestions, question.category, question.part_of_speech]);
    

    useEffect(() => {
        if (question) {
            generateOptions(question.japanese);
            setSelectedOption(null); // 新しい質問が表示されたときに選択をリセット
            setIsCorrect(null); // 正解/不正解の表示をリセット
        }
    }, [question, generateOptions]);

    const onOptionClick = (option) => {
        setSelectedOption(option);
        const correct = option === question.japanese;
        setIsCorrect(correct);

        // 1秒間の遅延を追加してから次の質問に進む
        setTimeout(() => {
            handleAnswer(option); // 正解か不正解かの処理を呼び出す
        }, 1000); // 1000ミリ秒（1秒）の遅延
    };

    if (!question || !question.word) {
        return (
            <Container className="text-center mt-5">
                <h5 className="text-danger">Error: Question data is missing or invalid.</h5>
            </Container>
        );
    }

    return (
        <div className="modern-container">
            <div className="modern-section">
                {/* Progress Bar */}
                <div style={{ 
                    background: 'var(--border-color)',
                    borderRadius: 'var(--radius-lg)',
                    height: '8px',
                    marginBottom: 'var(--spacing-2xl)',
                    overflow: 'hidden'
                }}>
                    <div style={{
                        background: 'var(--primary-gradient)',
                        height: '100%',
                        width: `${(currentQuestion / totalQuestions) * 100}%`,
                        borderRadius: 'var(--radius-lg)',
                        transition: 'width var(--transition-normal)'
                    }} />
                </div>

                {/* Question Header */}
                <div className="text-center fade-in-up" style={{ marginBottom: 'var(--spacing-2xl)' }}>
                    <div className="modern-badge modern-badge-info" style={{ marginBottom: 'var(--spacing-lg)' }}>
                        Question {currentQuestion} of {totalQuestions}
                    </div>
                    <h2 style={{ 
                        fontSize: '2.5rem',
                        background: 'var(--primary-gradient)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        marginBottom: 'var(--spacing-md)'
                    }}>
                        {question.word}
                    </h2>
                    <p style={{ 
                        fontSize: '1.25rem',
                        color: 'var(--text-secondary)'
                    }}>
                        の意味は？
                    </p>
                </div>

                {/* Options */}
                <div style={{ 
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: 'var(--spacing-lg)',
                    marginBottom: 'var(--spacing-2xl)'
                }}>
                    {options.map((option, index) => {
                        let buttonClass = 'modern-btn modern-btn-secondary';
                        let buttonStyle = { 
                            width: '100%', 
                            height: '60px',
                            fontSize: '1.1rem',
                            animationDelay: `${index * 0.1}s`
                        };

                        if (selectedOption === option) {
                            if (isCorrect) {
                                buttonClass = 'modern-btn modern-btn-success';
                                buttonStyle.boxShadow = '0 0 20px rgba(79, 172, 254, 0.4)';
                            } else {
                                buttonClass = 'modern-btn modern-btn-danger';
                                buttonStyle.boxShadow = '0 0 20px rgba(255, 107, 107, 0.4)';
                            }
                        }

                        return (
                            <button
                                key={index}
                                className={`${buttonClass} fade-in-up`}
                                style={buttonStyle}
                                onClick={() => onOptionClick(option)}
                                disabled={selectedOption !== null}
                            >
                                {option}
                            </button>
                        );
                    })}
                </div>

                {/* Result Alert */}
                {selectedOption !== null && (
                    <div className={`modern-alert ${isCorrect ? 'modern-alert-success' : 'modern-alert-danger'} fade-in-up`}>
                        <div style={{ 
                            fontSize: '1.1rem',
                            fontWeight: '600'
                        }}>
                            {isCorrect ? '🎉 正解です！' : `❌ 不正解です。正解は「${question.japanese}」です。`}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Question;
